import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Modal } from '@/components/ui/modal'
import { Pagination } from '@/components/ui/data-table'
import { toast } from 'sonner'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  getInboxAnnouncement,
  listInboxAnnouncements,
  markAnnouncementRead,
  notifyAnnouncementChanged,
  type AnnouncementItem,
} from '../../services/announcements'

const priorityLabels = { NORMAL: '普通', IMPORTANT: '重要', URGENT: '緊急' }
const priorityColors = {
  NORMAL: 'bg-slate-100 text-slate-700',
  IMPORTANT: 'bg-amber-50 text-amber-800',
  URGENT: 'bg-red-50 text-red-800',
}

export default function AnnouncementCenter() {
  const [params, setParams] = useSearchParams()
  const [items, setItems] = useState<AnnouncementItem[]>([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [detail, setDetail] = useState<AnnouncementItem | null>(null)

  const load = async () => {
    try {
      const response = await listInboxAnnouncements({
        page,
        size: 20,
        unreadOnly,
      })
      setItems(response.data.items)
      setTotal(response.data.pagination.total)
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : '載入公告失敗')
    }
  }

  const openDetail = async (id: string) => {
    try {
      const response = await getInboxAnnouncement(id)
      setDetail(response.data)
      await markAnnouncementRead(id)
      notifyAnnouncementChanged()
      await load()
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : '載入公告失敗')
    }
  }

  useEffect(() => {
    load()
  }, [page, unreadOnly])
  useEffect(() => {
    const id = params.get('id')
    if (id) openDetail(id)
  }, [params])

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">公告中心</h1>
        <div
          role="group"
          aria-label="公告篩選"
          className="flex gap-1 rounded-lg border border-border bg-card p-1"
        >
          {[
            ['all', '全部'],
            ['unread', '未讀'],
          ].map(([value, label]) => (
            <Button
              key={value}
              size="sm"
              aria-pressed={unreadOnly === (value === 'unread')}
              variant={
                unreadOnly === (value === 'unread') ? 'default' : 'ghost'
              }
              onClick={() => {
                setUnreadOnly(value === 'unread')
                setPage(1)
              }}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
      <Card>
        <CardContent>
          {items.length ? (
            <ul className="divide-y divide-border">
              {items.map((item) => (
                <li key={item.id}>
                  <Button
                    variant="ghost"
                    className="announcement-list-button h-auto w-full min-h-14 whitespace-normal px-4 py-3 text-left"
                    onClick={() => openDetail(item.id)}
                  >
                    <span className="announcement-list-title">
                      {!item.readAt && (
                        <span
                          aria-label="未讀"
                          className="size-2 shrink-0 rounded-full bg-primary"
                        />
                      )}
                      <span>{item.title}</span>
                      <span
                        className={`shrink-0 rounded px-2 py-0.5 text-xs ${priorityColors[item.priority]}`}
                      >
                        {priorityLabels[item.priority]}
                      </span>
                    </span>
                    <span className="announcement-list-time text-xs text-muted-foreground">
                      {new Date(item.visibleAt).toLocaleString()}
                    </span>
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-12 text-center text-muted-foreground">
              目前沒有公告
            </p>
          )}
          <Pagination
            page={page}
            size={20}
            total={total}
            onChange={(next) => setPage(next)}
          />
        </CardContent>
      </Card>
      <Modal
        open={!!detail}
        onOpenChange={(open) => {
          if (!open) {
            setDetail(null)
            setParams({})
          }
        }}
        title={detail?.title}
      >
        <p className="announcement-body text-sm">{detail?.content}</p>
      </Modal>
    </section>
  )
}
